import { FC, useState, useEffect, useRef } from 'react';
import { bossTier2CollectImg, bossTier3CollectImg, bossTier4CollectImg, bossTier5CollectImg, bossTier6CollectImg, bossTier7CollectImg } from '../services/bossImg';
import { yearPNG, crimePNG, respectPNG, gruntPNG } from '../services/tokenImg';
import { getRecentJackpots, JackpotEvent } from '../services/program';

const JackpotFeed: FC = () => {
  const [jackpots, setJackpots] = useState<JackpotEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Format time ago
  const formatTimeAgo = (timestamp: number): string => {
    if (timestamp === 0) return '';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  // Shorten wallet address
  const shortenWallet = (wallet: string): string => {
    if (wallet.length <= 10) return wallet;
    return `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;
  };

  // Get level info
  // gameType 0-2 = Plinko L1/L2/L3, gameType 10-15 = Jackpot boss consume J2-J7
  const getLevelInfo = (gameType: number) => {
    switch (gameType) {
      case 0:
        return { name: 'L1', color: '#4CAF50', tokenIcon: crimePNG, bossIcon: bossTier2CollectImg };
      case 1:
        return { name: 'L2', color: '#2196F3', tokenIcon: respectPNG, bossIcon: bossTier3CollectImg };
      case 2:
        return { name: 'L3', color: '#9C27B0', tokenIcon: gruntPNG, bossIcon: bossTier4CollectImg };
      case 10:
        return { name: 'J2', color: '#FF9800', tokenIcon: yearPNG, bossIcon: bossTier2CollectImg };
      case 11:
        return { name: 'J3', color: '#FF9800', tokenIcon: crimePNG, bossIcon: bossTier3CollectImg };
      case 12:
        return { name: 'J4', color: '#FF9800', tokenIcon: respectPNG, bossIcon: bossTier4CollectImg };
      case 13:
        return { name: 'J5', color: '#FF9800', tokenIcon: null, bossIcon: bossTier5CollectImg };
      case 14:
        return { name: 'J6', color: '#FF9800', tokenIcon: null, bossIcon: bossTier6CollectImg };
      case 15:
        return { name: 'J7', color: '#FF9800', tokenIcon: null, bossIcon: bossTier7CollectImg };
      default:
        return { name: 'L1', color: '#4CAF50', tokenIcon: crimePNG, bossIcon: bossTier2CollectImg };
    }
  };

  // Track whether game is playing to pause polling
  const pausedRef = useRef(false);

  // Fetch jackpots on mount and refresh every 10 seconds (paused during gameplay)
  useEffect(() => {
    let mounted = true;

    const fetchJackpots = async () => {
      if (pausedRef.current) return;
      try {
        const results = await getRecentJackpots(20);
        if (mounted) {
          setJackpots(results);
          setLoading(false);
        }
      } catch {
        if (mounted) setLoading(false);
      }
    };

    fetchJackpots();
    const interval = setInterval(fetchJackpots, 10000);

    // Pause polling during gameplay to avoid frame drops
    const handlePause = () => { pausedRef.current = true; };
    const handleResume = () => { pausedRef.current = false; };
    window.addEventListener('plinko-playing', handlePause);
    window.addEventListener('plinko-stopped', handleResume);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('plinko-playing', handlePause);
      window.removeEventListener('plinko-stopped', handleResume);
    };
  }, []);

  // Listen for jackpot-won event from PlinkoBoard to refresh immediately
  useEffect(() => {
    const handleJackpotWon = () => {
      // Small delay to let the transaction finalize on-chain
      setTimeout(async () => {
        try {
          const results = await getRecentJackpots(20);
          setJackpots(results);
        } catch { /* ignore */ }
      }, 2000);
    };
    window.addEventListener('jackpot-won', handleJackpotWon);
    return () => window.removeEventListener('jackpot-won', handleJackpotWon);
  }, []);

  // Update timestamps every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setJackpots(prev => [...prev]);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="jackpot-feed">
      <div className="jackpot-feed-header">
        <span className="jackpot-feed-title">Recent Jackpots</span>
      </div>
      <div className="jackpot-feed-list">
        {loading ? (
          <div className="jackpot-feed-empty">Loading...</div>
        ) : jackpots.length === 0 ? (
          <div className="jackpot-feed-empty">No jackpots yet</div>
        ) : (
          jackpots.map((jackpot) => {
            const levelInfo = getLevelInfo(jackpot.gameType);
            return (
              <div key={jackpot.signature} className="jackpot-feed-item">
                <div className="jackpot-feed-row">
                  <span className="jackpot-wallet">{shortenWallet(jackpot.player)}</span>
                  <span className="jackpot-level" style={{ backgroundColor: levelInfo.color }}>{levelInfo.name}</span>
                </div>
                <div className="jackpot-feed-row">
                  {levelInfo.tokenIcon && (
                    <span className="jackpot-won">
                      +{jackpot.tokensWon.toFixed(jackpot.tokensWon % 1 === 0 ? 0 : 2)}
                      <img src={levelInfo.tokenIcon} alt="" className="jackpot-token-icon" />
                    </span>
                  )}
                  <span className="jackpot-boss" style={{ marginLeft: 'auto' }}>
                    +{jackpot.amount}
                    <img src={levelInfo.bossIcon} alt="" className="jackpot-boss-icon" />
                  </span>
                </div>
                <div className="jackpot-feed-row">
                  <span className="jackpot-time">{formatTimeAgo(jackpot.timestamp)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default JackpotFeed;
